import AudioFeedbackPlayer from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/AudioFeedbackPlayer';
import { FeedbackSummary } from '@modules/feedback/components/FeedbackSummary';
import { isFeedbackSummaryPresent } from '@modules/feedback/components/FeedbackSummary/utils';
import { useIsFeedbackOnFeedbackV2Enabled } from '@modules/feedback/hooks';
import { ChatRounded } from '@mui/icons-material';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useMeasure } from 'react-use';
import {
  Feedback,
  FeedbackContentType,
  FeedbackLabel,
} from 'v2/core/entities/feedback';
import { FeedbackDetailsConversation } from '../FeedbackDetailsConversation';

interface FeedbackDetailsLeftPanelProps {
  feedback: Feedback;
  selectedLabels: FeedbackLabel[];
  showOriginalLanguage: boolean;
  setSelectedLabels: (selectedLabels: FeedbackLabel[]) => void;
}

export const FeedbackDetailsLeftPanel = ({
  feedback,
  selectedLabels,
  showOriginalLanguage,
  setSelectedLabels,
}: FeedbackDetailsLeftPanelProps) => {
  const isSummaryPresent = isFeedbackSummaryPresent(feedback);
  const { isFeedbackOnFeedbackV2Enabled } = useIsFeedbackOnFeedbackV2Enabled();

  const [audioPlayerRef, { height: audioPlayerElHeight }] = useMeasure();
  const [conversationSummaryRef, { height: conversationSummaryElHeight }] =
    useMeasure();

  const getConversationViewHeight = (offset: number) => {
    if (conversationSummaryElHeight || audioPlayerElHeight) {
      return `calc(100vh - ${
        offset + (audioPlayerElHeight || conversationSummaryElHeight)
      }px)`;
    }
    return '100%';
  };

  const isAudioFeedback = feedback.content.type === FeedbackContentType.Audio;

  return (
    <Stack spacing={2} sx={{ height: '100%' }} justifyContent="space-between">
      {((isFeedbackOnFeedbackV2Enabled && !isAudioFeedback) ||
        isSummaryPresent) && (
        <Box ref={conversationSummaryRef}>
          <FeedbackSummary
            feedback={feedback}
            selectedLabels={selectedLabels}
          />
        </Box>
      )}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          pb: 2,
          height: '100%',
        }}
      >
        {(isFeedbackOnFeedbackV2Enabled || isSummaryPresent) &&
          !isAudioFeedback && (
            <Box
              sx={{
                px: 3,
                pt: 2,
                pb: 1,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <ChatRounded
                  sx={{
                    color: 'feedback.label',
                  }}
                />
                <Typography variant="body1" color="feedback.label">
                  Feedback
                </Typography>
              </Stack>
            </Box>
          )}
        <Box
          p={2}
          height="100%"
          sx={{
            overflow: 'auto',
            height: getConversationViewHeight(200),
          }}
        >
          <FeedbackDetailsConversation
            feedback={feedback}
            selectedLabels={selectedLabels}
            showOriginalLanguage={showOriginalLanguage}
          />
        </Box>
      </Paper>
      {isAudioFeedback && (
        <Box ref={audioPlayerRef}>
          <AudioFeedbackPlayer
            selectedLabels={selectedLabels}
            setSelectedLabels={setSelectedLabels}
            feedback={feedback}
          />
        </Box>
      )}
    </Stack>
  );
};
